/*
 * Copyright Evollis 2026
 * All Rights Reserved.
 */
import "./AlgoanBankModal.scss"
import axios from "axios"
import React, { useCallback, useEffect, useState } from "react"
import { Button, Form, Modal } from "react-bootstrap"
import {loc} from "../../../../core/utils";

const LOADING_DURATION_MS = 10000
const PROGRESS_UPDATE_INTERVAL_MS = 50

/**
 * @typedef {Object} AlgoanBankAccount
 * @property {string} accountName
 * @property {string} bankName
 * @property {string} iban
 * @property {string} bic
 */

/**
 * Blocking modal for Algoan bank account selection when returning from Algoan journey.
 * Shows loading (progress bar 10s), then single account card or multiple selectable cards.
 */
/** Error codes returned by Algoan bank accounts API when errorCode is present */
const ALGOAN_ERROR_CODES = [
  "CURRENCY_NOT_SUPPORTED",
  "NO_CHECKING_ACCOUNT",
  "ACCOUNT_TYPE_NOT_SUPPORTED",
  "OWNERSHIP_NOT_SUPPORTED",
  "USAGE_NOT_SUPPORTED",
  "NOT_ENOUGH_TRANSACTIONS",
  "UNKNOWN"
]

export default function AlgoanBankModal({ show, tunnelData, onContinue }) {
  const [status, setStatus] = useState("loading") // 'loading' | 'loaded' | 'error' | 'openBankingError'
  const [banks, setBanks] = useState(/** @type {AlgoanBankAccount[]} */ ([]))
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [progress, setProgress] = useState(0)
  const [errorMessage, setErrorMessage] = useState(null)
  const [openBankingErrorCode, setOpenBankingErrorCode] = useState(/** @type {string | null} */ (null))

  const opl = tunnelData?.opl
  const apiUrl = tunnelData?.apiUrl
  const accessToken = tunnelData?.access_token

  const fetchBanks = useCallback(async (cancelToken) => {
      if (!apiUrl || !opl) return
      if (!accessToken) {
        setStatus("error")
        setErrorMessage(loc("algoanBankModal_errorMessage"))
        return
      }
      const url = `${apiUrl}middle-business-service/api/v2/algoan/${opl}/bank-accounts`
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        Accept: "*/*"
      }
      try {
        const response = await axios.get(url, { headers, cancelToken })
        const errorCode = response?.data?.errorCode
        if (errorCode) {
          setOpenBankingErrorCode(
            ALGOAN_ERROR_CODES.includes(errorCode) ? errorCode : "UNKNOWN"
          )
          setStatus("openBankingError")
          return
        }
        const accounts = response?.data?.accounts ?? []
        setBanks(Array.isArray(accounts) ? accounts : [])
        setStatus("loaded")
        if (accounts.length === 1) setSelectedIndex(0)
      } catch (err) {
        if (axios.isCancel(err)) return
        setStatus("error")
        setErrorMessage(loc("algoanBankModal_errorMessage"))
      }
    },
    [apiUrl, opl, accessToken]
  )

  // Progress bar animation over 10 seconds
  useEffect(() => {
    if (!show || status !== "loading") return
    const start = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - start
      const value = Math.min(100, (elapsed / LOADING_DURATION_MS) * 100)
      setProgress(value)
    }, PROGRESS_UPDATE_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [show, status])

  // Fetch banks when modal opens; cancel request on unmount or when show changes
  useEffect(() => {
    if (!show) return
    setStatus("loading")
    setBanks([])
    setSelectedIndex(null)
    setProgress(0)
    setErrorMessage(null)
    setOpenBankingErrorCode(null)
    const source = axios.CancelToken.source()
    fetchBanks(source.token)
    return () => source.cancel()
  }, [show, fetchBanks])

  const handleContinue = () => {
    if (!onContinue) return
    const selectedBank = banks.length === 1 ? banks[0] : banks[selectedIndex]
    if (!selectedBank) return
    onContinue({
      iban: selectedBank.iban,
      bic: selectedBank.bic,
      accountKey: selectedBank.accountKey,
      batchId: selectedBank.batchId
    })
  }

  const isSingleAccount = banks.length === 1
  const canContinue = isSingleAccount || (banks.length > 1 && selectedIndex !== null)

  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      centered
      className="algoan-bank-modal"
      dialogClassName="algoan-bank-modal__dialog"
      contentClassName="algoan-bank-modal__content"
    >
      <Modal.Body className="algoan-bank-modal__body">
        {status === "loading" && (
          <div className="algoan-bank-modal__loading">
            <div className="algoan-bank-modal__spinner" role="status" aria-label={loc("algoanBankModal_loadingAria")}>
              <span className="algoan-bank-modal__spinner-dot" />
              <span className="algoan-bank-modal__spinner-dot" />
              <span className="algoan-bank-modal__spinner-dot" />
            </div>
            <p className="algoan-bank-modal__loading-text">{loc("algoanBankModal_loadingText")}</p>
            <div className="algoan-bank-modal__progress-track">
              <div
                className="algoan-bank-modal__progress-fill"
                style={{ width: `${progress}%` }}
                role="progressbar"
                aria-valuenow={Math.round(progress)}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
        )}

        {status === "openBankingError" && openBankingErrorCode && (
          <div className="algoan-bank-modal__error">
            <p className="algoan-bank-modal__error-title">
              {loc(`algoanBankModal_${openBankingErrorCode}_title`)}
            </p>
            <p className="algoan-bank-modal__error-text">
              {loc(`algoanBankModal_${openBankingErrorCode}_message`)}
            </p>
            {tunnelData?.shopUrl && (
              <Button
                variant="outline-primary"
                onClick={() => {
                  window.location.href = tunnelData.shopUrl
                }}
              >
                {loc("button_close")}
              </Button>
            )}
          </div>
        )}

        {status === "error" && (
          <div className="algoan-bank-modal__error">
            <p className="algoan-bank-modal__error-text">{errorMessage}</p>
            <div className="algoan-bank-modal__error-actions">
              <Button variant="outline-primary" onClick={() => fetchBanks()}>
                {loc("algoanBankModal_retry")}
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => {
                  window.location.href = tunnelData.shopUrl
                }}
              >
                {loc("button_close")}
              </Button>
            </div>
          </div>
        )}

        {status === "loaded" && banks.length > 0 && (
          <>
            <h2 className="algoan-bank-modal__title">
              {isSingleAccount
                ? loc("algoanBankModal_titleSingle")
                : loc("algoanBankModal_titleMultiple")}
            </h2>

            {isSingleAccount ? (
              <div className="algoan-bank-modal__list">
                <div className="algoan-bank-modal__card algoan-bank-modal__card--single-row">
                  <div className="algoan-bank-modal__card-content">
                    <BankAccountCard bank={banks[0]} compact />
                  </div>
                </div>
              </div>
            ) : (
              <Form className="algoan-bank-modal__list">
                {banks.map((bank, index) => (
                  <div
                    key={index}
                    className={`algoan-bank-modal__card algoan-bank-modal__card--selectable ${
                      selectedIndex === index ? "algoan-bank-modal__card--selected" : ""
                    }`}
                    onClick={() => setSelectedIndex(index)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        setSelectedIndex(index)
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectedIndex === index}
                  >
                    <Form.Check
                      type="radio"
                      name="algoan-bank"
                      id={`algoan-bank-${index}`}
                      checked={selectedIndex === index}
                      onChange={() => setSelectedIndex(index)}
                      onClick={(e) => e.stopPropagation()}
                      className="algoan-bank-modal__radio"
                    />
                    <div className="algoan-bank-modal__card-content">
                      <BankAccountCard bank={bank} compact />
                    </div>
                  </div>
                ))}
              </Form>
            )}

            <div className="algoan-bank-modal__footer">
              <Button
                variant="success"
                className="algoan-bank-modal__continue btn-solid btn"
                onClick={handleContinue}
                disabled={!canContinue}
              >
                {loc("algoanBankModal_continue")}
              </Button>
            </div>
          </>
        )}

        {status === "loaded" && banks.length === 0 && (
          <div className="algoan-bank-modal__error">
            <p className="algoan-bank-modal__error-text">
              {loc("algoanBankModal_noAccount")}
            </p>
            <p className="algoan-bank-modal__error-text">
              {loc("algoanBankModal_noAccountAdvice")}
            </p>
            {tunnelData?.shopUrl && (
              <Button
                variant="outline-primary"
                onClick={() => {
                  window.location.href = tunnelData.shopUrl
                }}
              >
                {loc("button_close")}
              </Button>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

function BankAccountCard({ bank, compact = false }) {
  if (compact) {
    return (
      <div className="algoan-bank-modal__card-inner algoan-bank-modal__card-inner--compact">
        <div className="algoan-bank-modal__card-header">
          {(bank.accountName || bank.bankName) && (
            <span className="algoan-bank-modal__card-title">
              {[bank.accountName, bank.bankName].filter(Boolean).join(" · ")}
            </span>
          )}
        </div>
        {bank.iban && (
          <p className="algoan-bank-modal__card-line algoan-bank-modal__iban" title="IBAN">
            {bank.iban}
          </p>
        )}
      </div>
    )
  }

  return (
    <>
      {(bank.accountName || bank.bankName) && (
        <div className="algoan-bank-modal__card-row">
          {bank.accountName && <span className="algoan-bank-modal__card-value">{bank.accountName}</span>}
          {bank.accountName && bank.bankName && <span className="algoan-bank-modal__card-sep"> · </span>}
          {bank.bankName && (
            <span className="algoan-bank-modal__card-value algoan-bank-modal__card-value--muted">{bank.bankName}</span>
          )}
        </div>
      )}
      {bank.iban && (
        <>
          <p className="algoan-bank-modal__card-label">IBAN</p>
          <p className="algoan-bank-modal__card-value algoan-bank-modal__iban">{bank.iban}</p>
        </>
      )}
    </>
  )
}
